import { useState } from "react";
import { toast } from "sonner";
import { Mail, ArrowRight, Loader2, ShieldCheck, KeyRound } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { requestOtp, verifyOtp } from "@/lib/api";

export const LoginModal = () => {
  const { showLogin, setShowLogin, login } = useAuth();
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => { setStep("email"); setCode(""); setBusy(false); };

  const sendCode = async () => {
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return toast.error("Enter a valid email address.");
    setBusy(true);
    try {
      await requestOtp(email.trim().toLowerCase());
      toast.success("We emailed you a 6-digit code.");
      setStep("code");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send code. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    if (code.trim().length !== 6) return toast.error("Enter the 6-digit code.");
    setBusy(true);
    try {
      const res = await verifyOtp(email.trim().toLowerCase(), code.trim());
      login(res.user, res.token);
      toast.success("Signed in.");
      reset();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verification failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={showLogin} onOpenChange={(o) => { setShowLogin(o); if (!o) reset(); }}>
      <DialogContent className="max-w-md" data-testid="login-modal">
        <DialogHeader>
          <div className="mb-2 flex h-11 w-11 items-center justify-center rounded-sm bg-primary/10 text-primary">
            {step === "email" ? <Mail className="h-5 w-5" /> : <KeyRound className="h-5 w-5" />}
          </div>
          <DialogTitle className="font-title text-xl">
            {step === "email" ? "Sign in to estimate" : "Enter your code"}
          </DialogTitle>
          <DialogDescription>
            {step === "email"
              ? "You get 3 free estimates. Sign in with your email to continue."
              : `We sent a 6-digit code to ${email}. It expires in 10 minutes.`}
          </DialogDescription>
        </DialogHeader>

        {step === "email" ? (
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Email address</Label>
              <Input
                data-testid="login-email-input"
                type="email"
                className="mt-1"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendCode()}
                autoFocus
              />
            </div>
            <Button data-testid="send-code-button" onClick={sendCode} disabled={busy} className="h-11 w-full rounded-sm">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Send code <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <Label className="text-xs">6-digit code</Label>
              <Input
                data-testid="login-code-input"
                inputMode="numeric"
                maxLength={6}
                className="mt-1 text-center font-mono text-2xl tracking-[0.5em]"
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                onKeyDown={(e) => e.key === "Enter" && verify()}
                autoFocus
              />
            </div>
            <Button data-testid="verify-code-button" onClick={verify} disabled={busy} className="h-11 w-full rounded-sm">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Verify & continue
            </Button>
            <button
              onClick={() => setStep("email")}
              className="w-full text-center font-mono text-xs uppercase tracking-wide text-muted-foreground hover:text-foreground"
            >
              ← Use a different email
            </button>
          </div>
        )}

        <div className="flex items-center gap-2 border-t border-border pt-3 text-xs text-muted-foreground">
          <ShieldCheck className="h-3.5 w-3.5" /> Passwordless & secure. We never share your email.
        </div>
      </DialogContent>
    </Dialog>
  );
};
